import { Component, OnInit } from '@angular/core';
import { ControlaiService } from '../../services/controlai.service';
import { UserSessionService } from '../../services/user-session.service';
import { API_BASE_URL } from '../../services/api-base';

type FeedbackType = 'success' | 'error' | '';
type ValidationResult = 'PENDING' | 'CORRECT' | 'INCORRECT';
type LifecycleStatus = 'OPEN' | 'CLOSED';
type ControlAiTab = 'commands' | 'transactions';
type CommandStatusFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
type TransactionDetailTab = 'overview' | 'validation' | 'history';
type CommandModalType = 'form' | 'history' | 'dashboard';
type ControlchatScopeType = 'TENANT' | 'SECTOR' | 'DEPARTMENT' | 'USER';
type CommandFormTab = 'initial' | 'images' | 'flow' | 'summary';

interface ControlAiValidationDraft {
  validation_result: ValidationResult;
  validation_notes: string;
  ai_feedback: string;
}

interface ControlAiUploadedImage {
  name: string;
  url: string;
  mime_type?: string | null;
  size?: number | null;
}

interface ControlAiCommandVisualAsset extends ControlAiUploadedImage {
  description?: string | null;
  importance?: string | null;
  required_actions?: string | null;
}

interface ControlAiFlowButton {
  label: string;
  action: string;
  next_state: string;
  output: string;
}

interface ControlAiFlowStage {
  id: string;
  title: string;
  text: string;
  output: string;
  buttons: ControlAiFlowButton[];
}

interface ControlAiCommandScopeOption {
  id: number;
  name: string;
  email?: string;
  sector_id?: number | null;
  department_id?: number | null;
}

interface ParsedReceivedItems {
  text: string;
  images: ControlAiUploadedImage[];
}

@Component({
  selector: 'app-controlai',
  templateUrl: './controlai.component.html',
  styleUrls: ['./controlai.component.scss']
})
export class ControlaiComponent implements OnInit {
  readonly apiBaseUrl = API_BASE_URL;
  readonly transactionStatusOptions = ['PENDING', 'RUNNING', 'DONE', 'FAILED'];
  readonly transactionLifecycleOptions: LifecycleStatus[] = ['OPEN', 'CLOSED'];
  readonly transactionValidationOptions: ValidationResult[] = ['PENDING', 'CORRECT', 'INCORRECT'];

  tenantId: number | null = null;
  userId: number | null = null;
  activeTab: ControlAiTab = 'transactions';

  commandSearch = '';
  commandStatusFilter: CommandStatusFilter = 'ALL';
  transactionFilters = this.buildDefaultTransactionFilters();
  commandModalVisible = false;
  commandModalType: CommandModalType = 'form';
  commandFormTab: CommandFormTab = 'initial';
  commandImagesBlockCollapsed = false;
  loadingCommandDashboard = false;

  loadingCommands = false;
  loadingTransactions = false;
  loadingCommandScopeOptions = false;
  savingCommand = false;
  startingTransaction = false;
  savingTransactionScope = false;
  creatingTransaction = false;
  uploadingCommandImages = false;
  uploadingTransactionImages = false;
  draggingCommandImages = false;
  draggingTransactionImages = false;
  executingTransactionIds = new Set<number>();
  interactingTransactionIds = new Set<number>();
  updatingLifecycleIds = new Set<number>();
  deletingTransactionIds = new Set<number>();
  loadingTransactionHistoryIds = new Set<number>();
  expandedTransactionHistoryIds = new Set<number>();
  expandedTransactionIds = new Set<number>();
  savingValidationIds = new Set<number>();
  reprocessingTransactionId: number | null = null;

  commands: any[] = [];
  transactions: any[] = [];
  commandHistory: any[] = [];
  selectedCommandForHistory: any | null = null;
  selectedCommandForDashboard: any | null = null;
  commandDashboard: any | null = null;
  commandScopeOptions: {
    sectors: ControlAiCommandScopeOption[];
    departments: ControlAiCommandScopeOption[];
    users: ControlAiCommandScopeOption[];
  } = {
    sectors: [],
    departments: [],
    users: []
  };

  feedback = '';
  feedbackType: FeedbackType = '';

  commandForm: any = this.buildDefaultCommandForm();
  transactionForm: any = this.buildDefaultTransactionForm();
  validationDrafts: Record<number, ControlAiValidationDraft> = {};
  transactionHistoryMap: Record<number, any[]> = {};
  interactionDrafts: Record<number, string> = {};
  transactionDetailTabMap: Record<number, TransactionDetailTab> = {};

  constructor(
    private controlaiService: ControlaiService,
    private userSession: UserSessionService
  ) {}

  ngOnInit(): void {
    const user = this.userSession.getUser();
    this.tenantId = Number(user?.tenant_id || user?.tenant?.id || 0) || null;
    this.userId = Number(user?.id || user?.['user_id'] || 0) || null;

    if (!this.tenantId) {
      this.showFeedback('Tenant não encontrado na sessão.', 'error');
      return;
    }

    this.loadCommands();
    this.loadTransactions();
  }

  private buildDefaultCommandForm() {
    return {
      id: null,
      title: '',
      objective: '',
      available_inputs: '',
      request_description: '',
      visual_assets: [] as ControlAiCommandVisualAsset[],
      prompt_template: '',
      model: 'gpt-4o-mini',
      temperature: 0.2,
      is_dossier: false,
      controlchat_enabled: false,
      controlchat_scope_tenant: false,
      controlchat_sector_ids: [] as number[],
      controlchat_department_ids: [] as number[],
      controlchat_user_ids: [] as number[],
      assistant_flow_config: this.buildDefaultAssistantFlowConfig(),
      version: 1,
      active: true
    };
  }

  private buildDefaultAssistantFlowConfig(): ControlAiFlowStage[] {
    return [
      {
        id: 'AWAITING_START_CONFIRMATION',
        title: 'Início da conversa',
        text: 'Olá! Vamos iniciar uma nova análise agora?',
        output: '',
        buttons: [
          { label: 'Sim', action: 'START_YES', next_state: 'AWAITING_REFERENCE', output: 'Usuário confirmou início da análise.' },
          { label: 'Não', action: 'START_NO', next_state: 'ENDED', output: 'Usuário encerrou o fluxo antes de iniciar.' }
        ]
      },
      {
        id: 'AWAITING_REFERENCE',
        title: 'Coleta de referência',
        text: 'Perfeito. Informe o código de referência para abrir a análise.',
        output: '',
        buttons: []
      },
      {
        id: 'AWAITING_SCOPE',
        title: 'Recebimento de contexto',
        text: 'Envie os dados, texto ou imagens para contextualizar o pedido.',
        output: '',
        buttons: [
          { label: 'Adicionar mais contexto', action: 'SCOPE_MORE_YES', next_state: 'AWAITING_SCOPE', output: 'Usuário deseja complementar o escopo.' },
          { label: 'Finalizar escopo', action: 'SCOPE_MORE_NO', next_state: 'PROCESSING_SCOPE', output: 'Escopo finalizado e pronto para execução.' }
        ]
      },
      {
        id: 'AWAITING_VALIDATION_DECISION',
        title: 'Validação de decisão',
        text: 'Com base no resultado, selecione a decisão final da análise.',
        output: '',
        buttons: [
          { label: 'Aprovar sem ligação', action: 'VALIDATION_APPROVE_NO_CALL', next_state: 'AWAITING_VALIDATION_OBSERVATION', output: 'Decisão: aprovar sem ligação.' },
          { label: 'Aprovar com ligação', action: 'VALIDATION_APPROVE_WITH_CALL', next_state: 'AWAITING_VALIDATION_OBSERVATION', output: 'Decisão: aprovar com ligação.' },
          { label: 'Recusar sem ligação', action: 'VALIDATION_REJECT_NO_CALL', next_state: 'AWAITING_VALIDATION_OBSERVATION', output: 'Decisão: recusar sem ligação.' },
          { label: 'Recusar com ligação', action: 'VALIDATION_REJECT_WITH_CALL', next_state: 'AWAITING_VALIDATION_OBSERVATION', output: 'Decisão: recusar com ligação.' },
          { label: 'Realizar ligação', action: 'VALIDATION_CALL_FOR_DECISION', next_state: 'AWAITING_VALIDATION_OBSERVATION', output: 'Decisão: ligação para tomada final.' }
        ]
      },
      {
        id: 'AWAITING_RESTART_CONFIRMATION',
        title: 'Reiniciar conversa',
        text: 'Deseja iniciar uma nova análise agora?',
        output: '',
        buttons: [
          { label: 'Sim', action: 'RESTART_YES', next_state: 'AWAITING_REFERENCE', output: 'Usuário reiniciou o fluxo.' },
          { label: 'Não', action: 'RESTART_NO', next_state: 'ENDED', output: 'Usuário encerrou a conversa.' }
        ]
      }
    ];
  }

  private normalizeAssistantFlowConfig(value: any): ControlAiFlowStage[] {
    const fallback = this.buildDefaultAssistantFlowConfig();
    if (value === null || value === undefined) {
      return fallback;
    }

    let parsed = value;
    if (typeof parsed === 'string') {
      const trimmed = parsed.trim();
      if (!trimmed) {
        return fallback;
      }
      try {
        parsed = JSON.parse(trimmed);
      } catch (_error) {
        return fallback;
      }
    }

    if (!Array.isArray(parsed)) {
      return fallback;
    }

    const normalized = parsed
      .map((stage: any) => {
        if (!stage || typeof stage !== 'object') {
          return null;
        }
        const stageId = String(stage.id || stage.state || '').trim().toUpperCase();
        if (!stageId) {
          return null;
        }
        const buttonsSource = Array.isArray(stage.buttons) ? stage.buttons : [];
        const buttons = buttonsSource
          .map((button: any) => {
            if (!button || typeof button !== 'object') {
              return null;
            }
            const action = String(button.action || '').trim().toUpperCase();
            return {
              label: String(button.label || '').trim() || 'Novo botão',
              action: action || 'CUSTOM_ACTION',
              next_state: String(button.next_state || button.nextState || '').trim().toUpperCase(),
              output: String(button.output || '').trim()
            } as ControlAiFlowButton;
          })
          .filter(Boolean) as ControlAiFlowButton[];

        return {
          id: stageId,
          title: String(stage.title || '').trim() || stageId,
          text: String(stage.text || stage.assistant_text || '').trim(),
          output: String(stage.output || '').trim(),
          buttons
        } as ControlAiFlowStage;
      })
      .filter(Boolean) as ControlAiFlowStage[];

    return normalized.length ? normalized : fallback;
  }

  private buildDefaultTransactionForm() {
    return {
      transaction_id: null as number | null,
      transaction_started_at: null as string | null,
      scope_submitted_at: null as string | null,
      reference_id: '',
      command_id: null,
      facts_date: '',
      received_items: '',
      analysis_comment: '',
      images: [] as ControlAiUploadedImage[]
    };
  }

  private buildDefaultTransactionFilters() {
    return {
      reference_id: '',
      command_id: null as number | null,
      status: '',
      lifecycle_status: '',
      validation_result: ''
    };
  }

  private showFeedback(message: string, type: FeedbackType) {
    this.feedback = message;
    this.feedbackType = type;
  }

  clearFeedback() {
    this.feedback = '';
    this.feedbackType = '';
  }

  setActiveTab(tab: ControlAiTab) {
    this.activeTab = tab;
  }

  goToCommandWorkspace() {
    this.setActiveTab('commands');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goToTransactionWorkspace() {
    this.setActiveTab('transactions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  openNewCommandModal() {
    this.commandForm = this.buildDefaultCommandForm();
    this.resetCommandFormWizardState();
    this.loadCommandScopeOptions();
    this.selectedCommandForHistory = null;
    this.selectedCommandForDashboard = null;
    this.commandDashboard = null;
    this.loadingCommandDashboard = false;
    this.commandHistory = [];
    this.commandModalType = 'form';
    this.commandModalVisible = true;
  }

  closeCommandModal() {
    this.commandModalVisible = false;
    this.commandModalType = 'form';
    this.resetCommandFormWizardState();
    this.selectedCommandForHistory = null;
    this.selectedCommandForDashboard = null;
    this.commandDashboard = null;
    this.loadingCommandDashboard = false;
    this.commandHistory = [];
    this.commandForm = this.buildDefaultCommandForm();
  }

  private resetCommandFormWizardState() {
    this.commandFormTab = 'initial';
    this.commandImagesBlockCollapsed = false;
  }

  setCommandFormTab(tab: CommandFormTab) {
    this.commandFormTab = tab;
  }

  goToPreviousCommandFormTab() {
    if (this.commandFormTab === 'images') {
      this.commandFormTab = 'initial';
      return;
    }
    if (this.commandFormTab === 'flow') {
      this.commandFormTab = 'images';
      return;
    }
    if (this.commandFormTab === 'summary') {
      this.commandFormTab = 'flow';
    }
  }

  goToNextCommandFormTab() {
    if (this.commandFormTab === 'initial') {
      if (!this.commandForm.title?.trim() || !this.commandForm.objective?.trim() || !this.commandForm.prompt_template?.trim()) {
        this.showFeedback('Preencha título, objetivo e prompt base para avançar.', 'error');
        return;
      }
      this.commandFormTab = 'images';
      return;
    }
    if (this.commandFormTab === 'images') {
      this.commandFormTab = 'flow';
      return;
    }
    if (this.commandFormTab === 'flow') {
      this.commandFormTab = 'summary';
    }
  }

  toggleCommandImagesBlock() {
    this.commandImagesBlockCollapsed = !this.commandImagesBlockCollapsed;
  }

  get isCommandSummaryTab() {
    return this.commandFormTab === 'summary';
  }

  get commandFlowStages() {
    if (!Array.isArray(this.commandForm?.assistant_flow_config)) {
      this.commandForm.assistant_flow_config = this.buildDefaultAssistantFlowConfig();
    }
    return this.commandForm.assistant_flow_config as ControlAiFlowStage[];
  }

  addFlowStage() {
    const stages = this.commandFlowStages;
    const nextNumber = stages.length + 1;
    stages.push({
      id: `CUSTOM_STAGE_${nextNumber}`,
      title: `Nova etapa ${nextNumber}`,
      text: '',
      output: '',
      buttons: []
    });
  }

  removeFlowStage(stageIndex: number) {
    const stages = this.commandFlowStages;
    if (stageIndex < 0 || stageIndex >= stages.length) {
      return;
    }
    stages.splice(stageIndex, 1);
    if (!stages.length) {
      this.commandForm.assistant_flow_config = this.buildDefaultAssistantFlowConfig();
    }
  }

  addFlowButton(stageIndex: number) {
    const stages = this.commandFlowStages;
    const stage = stages[stageIndex];
    if (!stage) {
      return;
    }
    if (!Array.isArray(stage.buttons)) {
      stage.buttons = [];
    }
    stage.buttons.push({
      label: 'Novo botão',
      action: `ACTION_${stage.buttons.length + 1}`,
      next_state: '',
      output: ''
    });
  }

  removeFlowButton(stageIndex: number, buttonIndex: number) {
    const stages = this.commandFlowStages;
    const stage = stages[stageIndex];
    if (!stage || !Array.isArray(stage.buttons)) {
      return;
    }
    if (buttonIndex < 0 || buttonIndex >= stage.buttons.length) {
      return;
    }
    stage.buttons.splice(buttonIndex, 1);
  }

  get commandFormScopeSummary() {
    if (!this.commandForm.controlchat_enabled) {
      return 'Desligado';
    }
    const sectorTotal = this.normalizeScopeIdList(this.commandForm.controlchat_sector_ids).length;
    const departmentTotal = this.normalizeScopeIdList(this.commandForm.controlchat_department_ids).length;
    const userTotal = this.normalizeScopeIdList(this.commandForm.controlchat_user_ids).length;
    if (this.commandForm.controlchat_scope_tenant && !sectorTotal && !departmentTotal && !userTotal) {
      return 'Tenant inteiro';
    }
    const chunks: string[] = [];
    if (sectorTotal) {
      chunks.push(`Setores: ${sectorTotal}`);
    }
    if (departmentTotal) {
      chunks.push(`Departamentos: ${departmentTotal}`);
    }
    if (userTotal) {
      chunks.push(`Usuários: ${userTotal}`);
    }
    return chunks.length ? chunks.join(' · ') : 'Sem escopo definido';
  }

  resetTransactionForm() {
    this.transactionForm = this.buildDefaultTransactionForm();
    this.startingTransaction = false;
    this.savingTransactionScope = false;
    if (this.commands.length) {
      this.transactionForm.command_id = this.commands[0].id;
    }
  }

  get selectedTransactionCommand(): any | null {
    const commandId = Number(this.transactionForm?.command_id || 0);
    if (!commandId) {
      return null;
    }
    return this.commands.find((command) => Number(command?.id) === commandId) || null;
  }

  get isSelectedTransactionCommandDossier() {
    return Number(this.selectedTransactionCommand?.is_dossier) === 1 || this.selectedTransactionCommand?.is_dossier === true;
  }

  private populateTransactionDraft(transaction: any) {
    const parsed = this.parseReceivedItems(transaction?.received_items);
    this.transactionForm.transaction_id = Number(transaction?.id || 0) || null;
    this.transactionForm.transaction_started_at = transaction?.transaction_started_at || transaction?.created_at || null;
    this.transactionForm.scope_submitted_at = transaction?.scope_submitted_at || null;
    this.transactionForm.reference_id = String(transaction?.reference_id || '').trim();
    this.transactionForm.command_id = Number(transaction?.command_id || this.transactionForm.command_id || 0) || null;
    this.transactionForm.facts_date = String(transaction?.facts_date || '').slice(0, 10);
    this.transactionForm.received_items = parsed.text || '';
    this.transactionForm.analysis_comment = String(transaction?.analysis_comment || '').trim();
    this.transactionForm.images = Array.isArray(parsed.images) ? [...parsed.images] : [];
  }

  private normalizeScopeIdList(values: any[]): number[] {
    if (!Array.isArray(values)) {
      return [];
    }
    return Array.from(
      new Set(
        values
          .map((item) => Number(item))
          .filter((item) => Number.isFinite(item) && item > 0)
      )
    );
  }

  private parseControlchatScopesFromCommand(command: any): {
    tenant: boolean;
    sectorIds: number[];
    departmentIds: number[];
    userIds: number[];
  } {
    const scopes = Array.isArray(command?.controlchat_scopes) ? command.controlchat_scopes : [];
    const sectorIds: number[] = [];
    const departmentIds: number[] = [];
    const userIds: number[] = [];
    let tenant = false;

    scopes.forEach((scope: any) => {
      const scopeType = String(scope?.scope_type || '').trim().toUpperCase() as ControlchatScopeType;
      const scopeValue = Number(scope?.scope_value || 0);
      if (scopeType === 'TENANT') {
        tenant = true;
      } else if (scopeType === 'SECTOR' && scopeValue > 0) {
        sectorIds.push(scopeValue);
      } else if (scopeType === 'DEPARTMENT' && scopeValue > 0) {
        departmentIds.push(scopeValue);
      } else if (scopeType === 'USER' && scopeValue > 0) {
        userIds.push(scopeValue);
      }
    });

    const hasSpecificScopes = sectorIds.length > 0 || departmentIds.length > 0 || userIds.length > 0;

    return {
      tenant: hasSpecificScopes ? false : tenant || !scopes.length,
      sectorIds: this.normalizeScopeIdList(sectorIds),
      departmentIds: this.normalizeScopeIdList(departmentIds),
      userIds: this.normalizeScopeIdList(userIds)
    };
  }

  private buildControlchatScopesPayload() {
    if (!this.commandForm.controlchat_enabled) {
      return [];
    }

    const scopes: Array<{ scope_type: ControlchatScopeType; scope_value: number | null }> = [];

    const sectorIds = this.normalizeScopeIdList(this.commandForm.controlchat_sector_ids);
    const departmentIds = this.normalizeScopeIdList(this.commandForm.controlchat_department_ids);
    const userIds = this.normalizeScopeIdList(this.commandForm.controlchat_user_ids);
    const hasSpecificScopes = sectorIds.length > 0 || departmentIds.length > 0 || userIds.length > 0;

    if (this.commandForm.controlchat_scope_tenant && !hasSpecificScopes) {
      scopes.push({ scope_type: 'TENANT', scope_value: null });
    }

    sectorIds.forEach((id) => scopes.push({ scope_type: 'SECTOR', scope_value: id }));
    departmentIds.forEach((id) => scopes.push({ scope_type: 'DEPARTMENT', scope_value: id }));
    userIds.forEach((id) => scopes.push({ scope_type: 'USER', scope_value: id }));

    return scopes;
  }

  loadCommandScopeOptions(force = false) {
    if (!this.tenantId) {
      return;
    }
    if (!force && this.commandScopeOptions.sectors.length && this.commandScopeOptions.users.length) {
      return;
    }

    this.loadingCommandScopeOptions = true;
    this.controlaiService.getCommandScopeOptions(this.tenantId).subscribe({
      next: (response) => {
        this.loadingCommandScopeOptions = false;
        this.commandScopeOptions = {
          sectors: Array.isArray(response?.sectors) ? response.sectors : [],
          departments: Array.isArray(response?.departments) ? response.departments : [],
          users: Array.isArray(response?.users) ? response.users : []
        };
      },
      error: (err) => {
        this.loadingCommandScopeOptions = false;
        this.showFeedback(err?.error?.error || 'Não foi possível carregar opções de escopo do ControlChat.', 'error');
      }
    });
  }

  toggleCommandScope(scopeType: ControlchatScopeType, scopeValue: number, checked: boolean) {
    const numericValue = Number(scopeValue || 0);
    if (!numericValue) {
      return;
    }

    const fieldMap: Record<Exclude<ControlchatScopeType, 'TENANT'>, keyof typeof this.commandForm> = {
      SECTOR: 'controlchat_sector_ids',
      DEPARTMENT: 'controlchat_department_ids',
      USER: 'controlchat_user_ids'
    };
    if (scopeType === 'TENANT') {
      this.commandForm.controlchat_scope_tenant = checked;
      if (checked) {
        this.commandForm.controlchat_sector_ids = [];
        this.commandForm.controlchat_department_ids = [];
        this.commandForm.controlchat_user_ids = [];
      }
      return;
    }

    const field = fieldMap[scopeType];
    const current = this.normalizeScopeIdList(this.commandForm[field] || []);
    const next = checked
      ? Array.from(new Set([...current, numericValue]))
      : current.filter((id) => id !== numericValue);
    this.commandForm[field] = next;

    if (checked) {
      this.commandForm.controlchat_scope_tenant = false;
    }
  }

  isCommandScopeSelected(scopeType: Exclude<ControlchatScopeType, 'TENANT'>, scopeValue: number) {
    const numericValue = Number(scopeValue || 0);
    if (!numericValue) {
      return false;
    }
    const fieldMap: Record<Exclude<ControlchatScopeType, 'TENANT'>, keyof typeof this.commandForm> = {
      SECTOR: 'controlchat_sector_ids',
      DEPARTMENT: 'controlchat_department_ids',
      USER: 'controlchat_user_ids'
    };
    const field = fieldMap[scopeType];
    const current = this.normalizeScopeIdList(this.commandForm[field] || []);
    return current.includes(numericValue);
  }

  getCommandScopeSummary(command: any) {
    const scopes = Array.isArray(command?.controlchat_scopes) ? command.controlchat_scopes : [];
    if (!Number(command?.controlchat_enabled)) {
      return 'Desligado';
    }
    if (!scopes.length) {
      return 'Tenant inteiro';
    }
    const labelMap: Record<string, string> = {
      TENANT: 'Tenant',
      SECTOR: 'Setor',
      DEPARTMENT: 'Departamento',
      USER: 'Usuário'
    };
    const counts: Record<string, number> = {};
    scopes.forEach((scope: any) => {
      const type = String(scope?.scope_type || '').toUpperCase();
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.keys(counts)
      .map((type) => `${labelMap[type] || type}: ${counts[type]}`)
      .join(' · ');
  }

  private parseCommandVisualAssets(value: any): ControlAiCommandVisualAsset[] {
    if (value === null || value === undefined) {
      return [];
    }

    let parsed = value;
    if (typeof parsed === 'string') {
      const trimmed = parsed.trim();
      if (!trimmed) {
        return [];
      }
      try {
        parsed = JSON.parse(trimmed);
      } catch (_error) {
        return [];
      }
    }

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((asset: any) => {
        if (!asset || typeof asset !== 'object') {
          return null;
        }
        const url = String(asset.url || asset.path || asset.file_path || asset.src || '').trim();
        if (!url) {
          return null;
        }
        return {
          name: String(asset.name || url.split('/').pop() || 'imagem').trim(),
          url,
          mime_type: asset.mime_type || asset.mimetype || null,
          size: Number(asset.size || 0) || null,
          description: String(asset.description || '').trim() || null,
          importance: String(asset.importance || '').trim() || null,
          required_actions: String(asset.required_actions || '').trim() || null
        } as ControlAiCommandVisualAsset;
      })
      .filter(Boolean) as ControlAiCommandVisualAsset[];
  }

  private buildCommandVisualAssetsPayload() {
    const assets = Array.isArray(this.commandForm.visual_assets) ? this.commandForm.visual_assets : [];
    return assets
      .map((asset: ControlAiCommandVisualAsset) => {
        const url = String(asset?.url || '').trim();
        if (!url) {
          return null;
        }
        return {
          name: String(asset?.name || url.split('/').pop() || 'imagem').trim(),
          url,
          mime_type: asset?.mime_type || null,
          size: Number(asset?.size || 0) || null,
          description: String(asset?.description || '').trim() || null,
          importance: String(asset?.importance || '').trim() || null,
          required_actions: String(asset?.required_actions || '').trim() || null
        };
      })
      .filter(Boolean);
  }

  private parseReceivedItems(value: any): ParsedReceivedItems {
    if (value === null || value === undefined) {
      return { text: '', images: [] };
    }

    let parsed = value;
    if (typeof parsed === 'string') {
      const trimmed = parsed.trim();
      if (!trimmed) {
        return { text: '', images: [] };
      }
      try {
        parsed = JSON.parse(trimmed);
      } catch (_error) {
        return { text: parsed, images: [] };
      }
    }

    if (parsed && typeof parsed === 'object') {
      const textCandidates = [parsed.text, parsed.prompt, parsed.context, parsed.received_items, parsed.input];
      const text = textCandidates.find((item) => typeof item === 'string' && item.trim()) || '';

      const imagesSource = Array.isArray(parsed.images)
        ? parsed.images
        : Array.isArray(parsed.files)
        ? parsed.files
        : [];

      const images = imagesSource
        .map((image: any) => {
          if (!image) {
            return null;
          }
          if (typeof image === 'string') {
            return {
              name: image.split('/').pop() || 'imagem',
              url: image
            } as ControlAiUploadedImage;
          }
          const url = image.url || image.path || image.file_path || image.src;
          if (!url) {
            return null;
          }
          return {
            name: image.name || String(url).split('/').pop() || 'imagem',
            url: String(url),
            mime_type: image.mime_type || image.mimetype || null,
            size: Number(image.size || 0) || null
          } as ControlAiUploadedImage;
        })
        .filter(Boolean) as ControlAiUploadedImage[];

      if (text || images.length) {
        return { text, images };
      }

      return { text: JSON.stringify(parsed, null, 2), images: [] };
    }

    return { text: String(value), images: [] };
  }

  private enrichTransactionForView(transaction: any) {
    const parsed = this.parseReceivedItems(transaction?.received_items);
    return {
      ...transaction,
      _receivedText: parsed.text || '-',
      _receivedImages: parsed.images || []
    };
  }

  resolveImageUrl(urlLike: string | null | undefined) {
    const value = String(urlLike || '').trim();
    if (!value) {
      return '';
    }
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) {
      return value;
    }
    const normalized = value.startsWith('/') ? value : `/${value}`;
    return `${this.apiBaseUrl}${normalized}`;
  }

  openCommandImagePicker(fileInput: HTMLInputElement) {
    fileInput?.click();
  }

  onCommandImagesDragOver(event: DragEvent) {
    event.preventDefault();
    this.draggingCommandImages = true;
  }

  onCommandImagesDragLeave(event: DragEvent) {
    event.preventDefault();
    this.draggingCommandImages = false;
  }

  onCommandImagesDrop(event: DragEvent) {
    event.preventDefault();
    this.draggingCommandImages = false;
    const files = Array.from(event.dataTransfer?.files || []);
    this.uploadCommandImages(files);
  }

  onCommandImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input?.files || []);
    this.uploadCommandImages(files);
    input.value = '';
  }

  removeCommandVisualAsset(index: number) {
    if (!Array.isArray(this.commandForm.visual_assets)) {
      this.commandForm.visual_assets = [];
      return;
    }
    this.commandForm.visual_assets.splice(index, 1);
  }

  private uploadCommandImages(files: File[]) {
    if (!this.tenantId || !this.userId) {
      this.showFeedback('Sessão inválida para enviar imagens do comando.', 'error');
      return;
    }

    const imageFiles = files.filter((file) => String(file.type || '').startsWith('image/'));
    if (!imageFiles.length) {
      return;
    }

    const formData = new FormData();
    imageFiles.forEach((file) => formData.append('images', file));
    formData.append('tenant_id', String(this.tenantId));
    formData.append('user_id', String(this.userId));

    this.uploadingCommandImages = true;
    this.controlaiService.uploadCommandImages(formData).subscribe({
      next: (response) => {
        this.uploadingCommandImages = false;
        const uploaded = Array.isArray(response?.files) ? response.files : [];
        const currentAssets = Array.isArray(this.commandForm.visual_assets) ? this.commandForm.visual_assets : [];
        const mapped = uploaded.map((asset: any) => ({
          name: asset.name || 'imagem',
          url: asset.url,
          mime_type: asset.mime_type || null,
          size: Number(asset.size || 0) || null,
          description: '',
          importance: '',
          required_actions: ''
        }));
        this.commandForm.visual_assets = [...currentAssets, ...mapped];
        if (mapped.length) {
          this.showFeedback(`${mapped.length} imagem(ns) adicionada(s) ao mapa visual do comando.`, 'success');
        }
      },
      error: (err) => {
        this.uploadingCommandImages = false;
        this.showFeedback(err?.error?.error || 'Falha ao enviar imagens do comando.', 'error');
      }
    });
  }

  openTransactionImagePicker(fileInput: HTMLInputElement) {
    if (!this.isTransactionDraftActive) {
      this.showFeedback('Inicie uma nova transação para anexar imagens.', 'error');
      return;
    }
    fileInput?.click();
  }

  onTransactionImagesDragOver(event: DragEvent) {
    if (!this.isTransactionDraftActive) {
      return;
    }
    event.preventDefault();
    this.draggingTransactionImages = true;
  }

  onTransactionImagesDragLeave(event: DragEvent) {
    if (!this.isTransactionDraftActive) {
      return;
    }
    event.preventDefault();
    this.draggingTransactionImages = false;
  }

  onTransactionImagesDrop(event: DragEvent) {
    if (!this.isTransactionDraftActive) {
      return;
    }
    event.preventDefault();
    this.draggingTransactionImages = false;
    const files = Array.from(event.dataTransfer?.files || []);
    this.uploadTransactionImages(files);
  }

  onTransactionImageSelected(event: Event) {
    if (!this.isTransactionDraftActive) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const files = Array.from(input?.files || []);
    this.uploadTransactionImages(files);
    input.value = '';
  }

  removeTransactionImage(index: number) {
    if (!Array.isArray(this.transactionForm.images)) {
      this.transactionForm.images = [];
      return;
    }
    this.transactionForm.images.splice(index, 1);
  }

  private buildTransactionReceivedItemsPayload() {
    const text = String(this.transactionForm.received_items || '').trim();
    const images = Array.isArray(this.transactionForm.images) ? this.transactionForm.images : [];

    if (images.length) {
      return {
        text: text || null,
        images
      };
    }
    return text || null;
  }

  private uploadTransactionImages(files: File[]) {
    if (!this.tenantId || !this.userId) {
      this.showFeedback('Sessão inválida para enviar imagens.', 'error');
      return;
    }
    if (!this.isTransactionDraftActive) {
      this.showFeedback('Inicie uma nova transação antes de enviar imagens.', 'error');
      return;
    }

    const imageFiles = files.filter((file) => String(file.type || '').startsWith('image/'));
    if (!imageFiles.length) {
      return;
    }

    const formData = new FormData();
    imageFiles.forEach((file) => formData.append('images', file));
    formData.append('tenant_id', String(this.tenantId));
    formData.append('user_id', String(this.userId));

    this.uploadingTransactionImages = true;
    this.controlaiService.uploadTransactionImages(formData).subscribe({
      next: (response) => {
        this.uploadingTransactionImages = false;
        const uploaded = Array.isArray(response?.files) ? response.files : [];
        const currentImages = Array.isArray(this.transactionForm.images) ? this.transactionForm.images : [];
        this.transactionForm.images = [...currentImages, ...uploaded];
        if (uploaded.length) {
          this.showFeedback(`${uploaded.length} imagem(ns) adicionada(s) na transação.`, 'success');
        }
      },
      error: (err) => {
        this.uploadingTransactionImages = false;
        this.showFeedback(err?.error?.error || 'Falha ao enviar imagens.', 'error');
      }
    });
  }

  loadCommands() {
    if (!this.tenantId) {
      return;
    }

    this.loadingCommands = true;
    const activeFilter =
      this.commandStatusFilter === 'ACTIVE'
        ? 1
        : this.commandStatusFilter === 'INACTIVE'
        ? 0
        : null;

    this.controlaiService.listCommands(this.tenantId, this.commandSearch, activeFilter).subscribe({
      next: (rows) => {
        this.commands = (rows || []).map((command: any) => ({
          ...command,
          visual_assets_parsed: this.parseCommandVisualAssets(command?.visual_assets)
        }));
        if (!this.transactionForm.command_id && this.commands.length) {
          this.transactionForm.command_id = this.commands[0].id;
        }
        if (
          this.transactionForm.command_id &&
          !this.commands.some((command) => Number(command.id) === Number(this.transactionForm.command_id))
        ) {
          this.transactionForm.command_id = this.commands.length ? this.commands[0].id : null;
        }
        this.loadingCommands = false;
      },
      error: (err) => {
        this.loadingCommands = false;
        this.showFeedback(err?.error?.error || 'Não foi possível carregar comandos.', 'error');
      }
    });
  }

  applyCommandFilters() {
    this.loadCommands();
  }

  clearCommandFilters() {
    this.commandSearch = '';
    this.commandStatusFilter = 'ALL';
    this.loadCommands();
  }

  saveCommand() {
    if (!this.tenantId || !this.userId) {
      this.showFeedback('Sessão inválida para salvar comando.', 'error');
      return;
    }

    if (!this.commandForm.title?.trim() || !this.commandForm.objective?.trim() || !this.commandForm.prompt_template?.trim()) {
      this.showFeedback('Preencha título, objetivo e prompt base.', 'error');
      return;
    }
    if (this.uploadingCommandImages) {
      this.showFeedback('Aguarde o upload das imagens do comando antes de salvar.', 'error');
      return;
    }

    const controlchatScopes = this.buildControlchatScopesPayload();
    if (this.commandForm.controlchat_enabled && !controlchatScopes.length) {
      this.showFeedback(
        'Selecione ao menos um escopo do ControlChat (tenant, setor, departamento ou usuário).',
        'error'
      );
      return;
    }

    this.savingCommand = true;
    const payload = {
      tenant_id: this.tenantId,
      title: this.commandForm.title,
      objective: this.commandForm.objective,
      available_inputs: this.commandForm.available_inputs || null,
      request_description: this.commandForm.request_description || null,
      visual_assets: this.buildCommandVisualAssetsPayload(),
      assistant_flow_config: this.commandFlowStages,
      prompt_template: this.commandForm.prompt_template,
      model: this.commandForm.model || 'gpt-4o-mini',
      temperature: Number(this.commandForm.temperature || 0.2),
      is_dossier: this.commandForm.is_dossier ? 1 : 0,
      controlchat_enabled: this.commandForm.controlchat_enabled ? 1 : 0,
      controlchat_scopes: controlchatScopes,
      active: this.commandForm.active ? 1 : 0,
      created_by: this.userId,
      updated_by: this.userId
    };

    const isEditing = !!this.commandForm.id;
    const request$ = isEditing
      ? this.controlaiService.updateCommand(Number(this.commandForm.id), payload)
      : this.controlaiService.createCommand(payload);

    request$.subscribe({
      next: () => {
        this.savingCommand = false;
        this.commandForm = this.buildDefaultCommandForm();
        this.commandModalVisible = false;
        this.commandModalType = 'form';
        this.showFeedback(isEditing ? 'Comando atualizado com sucesso.' : 'Comando criado com sucesso.', 'success');
        this.loadCommands();
      },
      error: (err) => {
        this.savingCommand = false;
        this.showFeedback(err?.error?.error || 'Não foi possível salvar o comando.', 'error');
      }
    });
  }

  editCommand(command: any) {
    this.loadCommandScopeOptions();
    this.resetCommandFormWizardState();
    this.selectedCommandForDashboard = null;
    this.commandDashboard = null;
    this.loadingCommandDashboard = false;
    const parsedScopes = this.parseControlchatScopesFromCommand(command);
    this.commandForm = {
      id: command.id,
      title: command.title || '',
      objective: command.objective || '',
      available_inputs: command.available_inputs || '',
      request_description: command.request_description || '',
      visual_assets: this.parseCommandVisualAssets(command.visual_assets),
      prompt_template: command.prompt_template || '',
      model: command.model || 'gpt-4o-mini',
      temperature: Number(command.temperature ?? 0.2),
      is_dossier: Number(command.is_dossier) === 1,
      controlchat_enabled: Number(command.controlchat_enabled) === 1,
      controlchat_scope_tenant: parsedScopes.tenant,
      controlchat_sector_ids: parsedScopes.sectorIds,
      controlchat_department_ids: parsedScopes.departmentIds,
      controlchat_user_ids: parsedScopes.userIds,
      assistant_flow_config: this.normalizeAssistantFlowConfig(command.assistant_flow_config),
      version: Number(command.version || 1) || 1,
      active: Number(command.active) === 1
    };
    this.commandModalType = 'form';
    this.commandModalVisible = true;
  }

  cancelEditCommand() {
    this.closeCommandModal();
  }

  openCommandHistory(command: any) {
    if (!this.tenantId || !command?.id) {
      return;
    }

    this.selectedCommandForDashboard = null;
    this.commandDashboard = null;
    this.loadingCommandDashboard = false;
    this.selectedCommandForHistory = command;
    this.commandHistory = [];
    this.commandModalType = 'history';
    this.commandModalVisible = true;

    this.controlaiService.getCommandHistory(command.id, this.tenantId).subscribe({
      next: (rows) => {
        this.commandHistory = rows || [];
      },
      error: (err) => {
        this.showFeedback(err?.error?.error || 'Não foi possível carregar histórico do comando.', 'error');
      }
    });
  }

  openCommandDashboard(command: any) {
    if (!this.tenantId || !command?.id) {
      return;
    }

    this.selectedCommandForHistory = null;
    this.commandHistory = [];
    this.selectedCommandForDashboard = command;
    this.commandDashboard = null;
    this.loadingCommandDashboard = true;
    this.commandModalType = 'dashboard';
    this.commandModalVisible = true;

    this.controlaiService.getCommandDashboard(command.id, this.tenantId).subscribe({
      next: (response) => {
        this.loadingCommandDashboard = false;
        this.commandDashboard = response || null;
      },
      error: (err) => {
        this.loadingCommandDashboard = false;
        this.showFeedback(err?.error?.error || 'Não foi possível carregar o dashboard do comando.', 'error');
      }
    });
  }

  closeCommandHistory() {
    this.closeCommandModal();
  }

  startTransaction() {
    if (!this.tenantId || !this.userId) {
      this.showFeedback('Sessão inválida para iniciar transação.', 'error');
      return;
    }

    if (!this.transactionForm.command_id) {
      this.showFeedback('Selecione um comando antes de iniciar a transação.', 'error');
      return;
    }

    if (this.isTransactionDraftActive) {
      this.showFeedback('Já existe uma transação em andamento. Salve o escopo atual antes de iniciar outra.', 'error');
      return;
    }

    this.startingTransaction = true;
    const referenceId = String(this.transactionForm.reference_id || '').trim();
    const payload = {
      tenant_id: this.tenantId,
      command_id: Number(this.transactionForm.command_id),
      reference_id: referenceId || null,
      created_by: this.userId
    };

    this.controlaiService.startTransaction(payload).subscribe({
      next: (created) => {
        this.startingTransaction = false;
        this.populateTransactionDraft(created);
        this.showFeedback(
          created?.reused_existing
            ? `Dossiê #${created?.id || this.transactionForm.transaction_id} reaberto. Complemente os fatos e salve novamente.`
            : this.transactionForm.transaction_id
            ? `Transação #${this.transactionForm.transaction_id} iniciada. Agora preencha o escopo e clique em salvar transação.`
            : 'Transação iniciada. Agora preencha o escopo e clique em salvar transação.',
          'success'
        );
        this.loadTransactions();
      },
      error: (err) => {
        this.startingTransaction = false;
        this.showFeedback(err?.error?.error || 'Não foi possível iniciar transação.', 'error');
      }
    });
  }

  createTransaction() {
    if (!this.tenantId || !this.userId) {
      this.showFeedback('Sessão inválida para salvar transação.', 'error');
      return;
    }
    if (!this.isTransactionDraftActive || !this.transactionForm.transaction_id) {
      this.showFeedback('Clique em "Realizar nova transação" para iniciar o processo.', 'error');
      return;
    }
    if (this.uploadingTransactionImages) {
      this.showFeedback('Aguarde o upload das imagens antes de salvar a transação.', 'error');
      return;
    }
    const referenceId = String(this.transactionForm.reference_id || '').trim();
    if (!referenceId) {
      this.showFeedback('Reference ID é obrigatório para salvar a transação.', 'error');
      return;
    }

    const parsed = this.buildTransactionReceivedItemsPayload();
    const hasText = typeof parsed === 'string' ? !!String(parsed).trim() : !!String(parsed?.text || '').trim();
    const imageList = Array.isArray(this.transactionForm.images) ? this.transactionForm.images : [];
    if (!hasText && !imageList.length) {
      this.showFeedback('Informe texto e/ou imagens para salvar o escopo da transação.', 'error');
      return;
    }
    if (this.isSelectedTransactionCommandDossier && !String(this.transactionForm.facts_date || '').trim()) {
      this.showFeedback('Informe a data dos fatos para transações do tipo dossiê.', 'error');
      return;
    }

    this.savingTransactionScope = true;
    this.creatingTransaction = true;
    this.controlaiService
      .saveTransactionScope(Number(this.transactionForm.transaction_id), {
        tenant_id: this.tenantId,
        user_id: this.userId,
        updated_by: this.userId,
        reference_id: referenceId,
        facts_date: this.isSelectedTransactionCommandDossier ? (this.transactionForm.facts_date || null) : null,
        received_items: parsed,
        analysis_comment: this.transactionForm.analysis_comment || null
      })
      .subscribe({
        next: (updatedTransaction) => {
          this.savingTransactionScope = false;
          this.creatingTransaction = false;
          this.showFeedback(
            `Transação #${updatedTransaction?.id || this.transactionForm.transaction_id} salva com sucesso. Agora você pode executar a IA na lista.`,
            'success'
          );
          this.replaceTransaction(updatedTransaction);
          this.resetTransactionForm();
          this.loadTransactions();
        },
        error: (err) => {
          this.savingTransactionScope = false;
          this.creatingTransaction = false;
          this.showFeedback(err?.error?.error || 'Não foi possível salvar o escopo da transação.', 'error');
        }
      });
  }

  loadTransactions() {
    if (!this.tenantId) {
      return;
    }

    this.loadingTransactions = true;
    this.controlaiService
      .listTransactions(this.tenantId, {
        command_id: this.transactionFilters.command_id || null,
        status: this.transactionFilters.status || null,
        lifecycle_status: this.transactionFilters.lifecycle_status || null,
        validation_result: this.transactionFilters.validation_result || null,
        reference_id: this.transactionFilters.reference_id || null
      })
      .subscribe({
      next: (rows) => {
        this.transactions = (rows || []).map((tx) => this.enrichTransactionForView(tx));
        const txIdSet = new Set<number>();
        this.transactions.forEach((tx) => {
          txIdSet.add(Number(tx.id));
          this.ensureValidationDraft(tx);
          if (!Object.prototype.hasOwnProperty.call(this.interactionDrafts, tx.id)) {
            this.interactionDrafts[tx.id] = '';
          }
        });

        Object.keys(this.interactionDrafts).forEach((key) => {
          if (!txIdSet.has(Number(key))) {
            delete this.interactionDrafts[Number(key)];
          }
        });
        Object.keys(this.transactionHistoryMap).forEach((key) => {
          if (!txIdSet.has(Number(key))) {
            delete this.transactionHistoryMap[Number(key)];
            this.expandedTransactionHistoryIds.delete(Number(key));
          }
        });
        this.expandedTransactionIds.forEach((id) => {
          if (!txIdSet.has(Number(id))) {
            this.expandedTransactionIds.delete(Number(id));
          }
        });
        Object.keys(this.transactionDetailTabMap).forEach((key) => {
          if (!txIdSet.has(Number(key))) {
            delete this.transactionDetailTabMap[Number(key)];
          }
        });
        this.loadingTransactions = false;
      },
      error: (err) => {
        this.loadingTransactions = false;
        this.showFeedback(err?.error?.error || 'Não foi possível carregar transações.', 'error');
      }
      });
  }

  applyTransactionFilters() {
    this.loadTransactions();
  }

  clearTransactionFilters() {
    this.transactionFilters = this.buildDefaultTransactionFilters();
    this.loadTransactions();
  }

  reprocessTransaction(transaction: any) {
    if (!this.tenantId || !this.userId || !transaction?.id || !transaction?.command_id) {
      this.showFeedback('Dados insuficientes para reprocessar a análise.', 'error');
      return;
    }
    if (this.reprocessingTransactionId !== null) {
      return;
    }

    const payload = {
      tenant_id: this.tenantId,
      command_id: Number(transaction.command_id),
      reference_id: this.buildReprocessReferenceId(transaction.reference_id),
      received_items: this.normalizeReceivedItemsForClone(transaction.received_items),
      analysis_comment: this.normalizeAnalysisCommentForClone(transaction.analysis_comment, transaction.id),
      created_by: this.userId
    };

    this.reprocessingTransactionId = Number(transaction.id);
    this.controlaiService.createTransaction(payload).subscribe({
      next: (created) => {
        this.showFeedback(
          `Reprocessamento iniciado. Nova transação #${created?.id || '-'} criada com os mesmos dados.`,
          'success'
        );
        if (created?.id) {
          this.expandedTransactionIds.add(Number(created.id));
        }
        this.loadTransactions();
      },
      error: (err) => {
        this.showFeedback(err?.error?.error || 'Não foi possível reprocessar a análise.', 'error');
      },
      complete: () => {
        this.reprocessingTransactionId = null;
      }
    });
  }

  executeTransaction(transaction: any) {
    if (!this.tenantId || !this.userId || !transaction?.id) {
      return;
    }

    this.executingTransactionIds.add(transaction.id);
    this.controlaiService
      .executeTransaction(transaction.id, {
        tenant_id: this.tenantId,
        user_id: this.userId,
        updated_by: this.userId
      })
      .subscribe({
        next: (updatedTransaction) => {
          this.executingTransactionIds.delete(transaction.id);
          this.showFeedback(`Transação #${transaction.id} executada com sucesso.`, 'success');
          this.replaceTransaction(updatedTransaction);
          this.loadTransactionHistory(transaction.id, true);
        },
        error: (err) => {
          this.executingTransactionIds.delete(transaction.id);
          this.showFeedback(err?.error?.error || 'Falha ao executar transação na IA.', 'error');
          this.loadTransactions();
        }
      });
  }

  ensureValidationDraft(transaction: any) {
    if (!transaction?.id) {
      return;
    }
    if (!this.validationDrafts[transaction.id]) {
      this.validationDrafts[transaction.id] = {
        validation_result: (transaction.validation_result as ValidationResult) || 'PENDING',
        validation_notes: transaction.validation_notes || '',
        ai_feedback: transaction.ai_feedback || ''
      };
      return;
    }

    const draft = this.validationDrafts[transaction.id];
    draft.validation_result = (transaction.validation_result as ValidationResult) || draft.validation_result;
    draft.validation_notes = transaction.validation_notes || draft.validation_notes;
    draft.ai_feedback = transaction.ai_feedback || draft.ai_feedback;
  }

  saveValidation(transaction: any) {
    if (!this.tenantId || !this.userId || !transaction?.id) {
      return;
    }

    const draft = this.validationDrafts[transaction.id];
    if (!draft) {
      return;
    }

    this.savingValidationIds.add(transaction.id);
    this.controlaiService
      .updateValidation(transaction.id, {
        tenant_id: this.tenantId,
        user_id: this.userId,
        updated_by: this.userId,
        validation_result: draft.validation_result,
        validation_notes: draft.validation_notes || null,
        ai_feedback: draft.ai_feedback || null
      })
      .subscribe({
        next: () => {
          this.savingValidationIds.delete(transaction.id);
          this.showFeedback(`Validação da transação #${transaction.id} atualizada.`, 'success');
          this.loadTransactions();
        },
        error: (err) => {
          this.savingValidationIds.delete(transaction.id);
          this.showFeedback(err?.error?.error || 'Não foi possível atualizar a validação.', 'error');
        }
      });
  }

  interactTransaction(transaction: any) {
    if (!this.tenantId || !this.userId || !transaction?.id) {
      return;
    }
    if (String(transaction.lifecycle_status || 'OPEN').toUpperCase() === 'CLOSED') {
      this.showFeedback('Transação encerrada. Reabra para continuar a interação.', 'error');
      return;
    }

    const commentText = String(this.interactionDrafts[transaction.id] || '').trim();
    if (!commentText) {
      this.showFeedback('Informe um comentário para continuar a análise.', 'error');
      return;
    }

    this.interactingTransactionIds.add(transaction.id);
    this.controlaiService
      .interactTransaction(transaction.id, {
        tenant_id: this.tenantId,
        user_id: this.userId,
        updated_by: this.userId,
        comment_text: commentText
      })
      .subscribe({
        next: (response) => {
          this.interactingTransactionIds.delete(transaction.id);
          this.interactionDrafts[transaction.id] = '';
          this.replaceTransaction(response?.transaction);
          const messages = Array.isArray(response?.messages) ? response.messages : [];
          if (messages.length) {
            this.transactionHistoryMap[transaction.id] = messages;
          } else {
            this.loadTransactionHistory(transaction.id, true);
          }
          this.expandedTransactionHistoryIds.add(transaction.id);
          this.showFeedback(`Interação registrada na transação #${transaction.id}.`, 'success');
        },
        error: (err) => {
          this.interactingTransactionIds.delete(transaction.id);
          this.showFeedback(err?.error?.error || 'Não foi possível interagir com a IA.', 'error');
          this.loadTransactions();
        }
      });
  }

  updateTransactionLifecycle(transaction: any, lifecycleStatus: LifecycleStatus) {
    if (!this.tenantId || !this.userId || !transaction?.id) {
      return;
    }

    this.updatingLifecycleIds.add(transaction.id);
    this.controlaiService
      .updateTransactionLifecycle(transaction.id, {
        tenant_id: this.tenantId,
        user_id: this.userId,
        updated_by: this.userId,
        lifecycle_status: lifecycleStatus
      })
      .subscribe({
        next: (updatedTransaction) => {
          this.updatingLifecycleIds.delete(transaction.id);
          this.replaceTransaction(updatedTransaction);
          this.loadTransactionHistory(transaction.id, true);
          this.expandedTransactionHistoryIds.add(transaction.id);
          this.showFeedback(
            lifecycleStatus === 'CLOSED'
              ? `Transação #${transaction.id} encerrada com sucesso.`
              : `Transação #${transaction.id} reaberta com sucesso.`,
            'success'
          );
        },
        error: (err) => {
          this.updatingLifecycleIds.delete(transaction.id);
          this.showFeedback(err?.error?.error || 'Não foi possível atualizar o status da transação.', 'error');
        }
      });
  }

  deleteTransaction(transaction: any) {
    if (!this.tenantId || !this.userId || !transaction?.id) {
      return;
    }

    const transactionId = Number(transaction.id);
    if (!transactionId || this.deletingTransactionIds.has(transactionId)) {
      return;
    }

    const referenceLabel = String(transaction?.reference_id || '').trim();
    const confirmed = window.confirm(
      `Confirma excluir a transação #${transactionId}${referenceLabel ? ` (${referenceLabel})` : ''}? Essa ação remove textos, histórico e arquivos da análise.`
    );
    if (!confirmed) {
      return;
    }

    this.deletingTransactionIds.add(transactionId);
    this.controlaiService
      .deleteTransaction(transactionId, {
        tenant_id: this.tenantId,
        user_id: this.userId,
        deleted_by: this.userId
      })
      .subscribe({
        next: (response) => {
          this.removeTransactionFromView(transactionId);
          const message =
            String(response?.message || '').trim() ||
            `Transação #${transactionId} excluída com sucesso.`;
          this.showFeedback(message, 'success');
        },
        error: (err) => {
          this.deletingTransactionIds.delete(transactionId);
          this.showFeedback(err?.error?.error || 'Não foi possível excluir a transação.', 'error');
        },
        complete: () => {
          this.deletingTransactionIds.delete(transactionId);
        }
      });
  }

  toggleTransactionHistory(transaction: any) {
    const transactionId = Number(transaction?.id || 0);
    if (!transactionId) {
      return;
    }
    const currentTab = this.getTransactionDetailTab(transactionId);
    this.setTransactionDetailTab(transactionId, currentTab === 'history' ? 'overview' : 'history');
  }

  toggleTransactionDetails(transaction: any) {
    const transactionId = Number(transaction?.id || 0);
    if (!transactionId) {
      return;
    }

    if (this.expandedTransactionIds.has(transactionId)) {
      this.expandedTransactionIds.delete(transactionId);
      this.expandedTransactionHistoryIds.delete(transactionId);
      delete this.transactionDetailTabMap[transactionId];
      return;
    }

    this.expandedTransactionIds.add(transactionId);
    this.transactionDetailTabMap[transactionId] = this.transactionDetailTabMap[transactionId] || 'overview';
  }

  setTransactionDetailTab(transactionId: number, tab: TransactionDetailTab) {
    const txId = Number(transactionId || 0);
    if (!txId) {
      return;
    }
    this.transactionDetailTabMap[txId] = tab;
    if (tab === 'history') {
      this.expandedTransactionHistoryIds.add(txId);
      this.loadTransactionHistory(txId, false);
      return;
    }
    this.expandedTransactionHistoryIds.delete(txId);
  }

  getTransactionDetailTab(transactionId: number): TransactionDetailTab {
    const txId = Number(transactionId || 0);
    if (!txId) {
      return 'overview';
    }
    return this.transactionDetailTabMap[txId] || 'overview';
  }

  loadTransactionHistory(transactionId: number, forceRefresh = false) {
    if (!this.tenantId || !transactionId) {
      return;
    }
    if (this.loadingTransactionHistoryIds.has(transactionId)) {
      return;
    }
    if (!forceRefresh && Array.isArray(this.transactionHistoryMap[transactionId])) {
      return;
    }

    this.loadingTransactionHistoryIds.add(transactionId);
    this.controlaiService.getTransactionHistory(transactionId, this.tenantId).subscribe({
      next: (rows) => {
        this.loadingTransactionHistoryIds.delete(transactionId);
        const historyRows = Array.isArray(rows) ? rows : [];
        this.transactionHistoryMap[transactionId] = historyRows;
        const index = this.transactions.findIndex((tx) => Number(tx.id) === Number(transactionId));
        if (index >= 0) {
          this.transactions[index] = {
            ...this.transactions[index],
            interactions_count: historyRows.length
          };
          this.transactions = [...this.transactions];
        }
      },
      error: () => {
        this.loadingTransactionHistoryIds.delete(transactionId);
      }
    });
  }

  private replaceTransaction(updatedTransaction: any) {
    if (!updatedTransaction?.id) {
      return;
    }
    const enriched = this.enrichTransactionForView(updatedTransaction);
    const index = this.transactions.findIndex((tx) => Number(tx.id) === Number(enriched.id));
    const existing = index >= 0 ? this.transactions[index] : null;
    const historyCount = Array.isArray(this.transactionHistoryMap[enriched.id])
      ? this.transactionHistoryMap[enriched.id].length
      : null;

    if (enriched.interactions_count === undefined || enriched.interactions_count === null) {
      enriched.interactions_count = historyCount ?? existing?.interactions_count ?? 0;
    }

    this.ensureValidationDraft(enriched);
    if (!Object.prototype.hasOwnProperty.call(this.interactionDrafts, enriched.id)) {
      this.interactionDrafts[enriched.id] = '';
    }

    if (index >= 0) {
      this.transactions[index] = enriched;
      this.transactions = [...this.transactions];
      return;
    }

    this.transactions = [enriched, ...this.transactions];
  }

  private removeTransactionFromView(transactionId: number) {
    const txId = Number(transactionId || 0);
    if (!txId) {
      return;
    }
    this.transactions = this.transactions.filter((tx) => Number(tx.id) !== txId);
    delete this.validationDrafts[txId];
    delete this.transactionHistoryMap[txId];
    delete this.interactionDrafts[txId];
    delete this.transactionDetailTabMap[txId];
    this.expandedTransactionIds.delete(txId);
    this.expandedTransactionHistoryIds.delete(txId);
    this.executingTransactionIds.delete(txId);
    this.interactingTransactionIds.delete(txId);
    this.updatingLifecycleIds.delete(txId);
    this.loadingTransactionHistoryIds.delete(txId);
    this.savingValidationIds.delete(txId);
    this.deletingTransactionIds.delete(txId);
  }

  getTransactionHistory(transactionId: number) {
    return this.transactionHistoryMap[transactionId] || [];
  }

  isHistoryVisible(transactionId: number) {
    return this.expandedTransactionHistoryIds.has(transactionId);
  }

  isTransactionExpanded(transactionId: number) {
    return this.expandedTransactionIds.has(transactionId);
  }

  isReprocessingTransaction(transactionId: number) {
    return this.reprocessingTransactionId === Number(transactionId);
  }

  get isReprocessingOverlayVisible() {
    return this.reprocessingTransactionId !== null;
  }

  isLoadingTransactionHistory(transactionId: number) {
    return this.loadingTransactionHistoryIds.has(transactionId);
  }

  isInteracting(transactionId: number) {
    return this.interactingTransactionIds.has(transactionId);
  }

  isUpdatingLifecycle(transactionId: number) {
    return this.updatingLifecycleIds.has(transactionId);
  }

  isDeleting(transactionId: number) {
    return this.deletingTransactionIds.has(transactionId);
  }

  getExecutionDurationLabel(transaction: any) {
    return this.getDurationLabel(
      transaction?.execution_started_at,
      transaction?.execution_finished_at,
      String(transaction?.status || '').toUpperCase() === 'RUNNING'
    );
  }

  getScopeBuildDurationLabel(transaction: any) {
    return this.getDurationLabel(transaction?.transaction_started_at, transaction?.scope_submitted_at, false);
  }

  getJourneyDurationLabel(transaction: any) {
    const finishedAt = transaction?.execution_finished_at || transaction?.executed_at || null;
    return this.getDurationLabel(
      transaction?.transaction_started_at,
      finishedAt,
      String(transaction?.status || '').toUpperCase() === 'RUNNING'
    );
  }

  formatHistoryPayload(payload: any) {
    if (payload === null || payload === undefined) {
      return '-';
    }
    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      if (!trimmed) {
        return '-';
      }
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch (_error) {
        return trimmed;
      }
    }
    try {
      return JSON.stringify(payload, null, 2);
    } catch (_error) {
      return String(payload);
    }
  }

  private toDate(value: string | null | undefined) {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private getDurationLabel(startValue: string | null | undefined, endValue: string | null | undefined, allowRunning: boolean) {
    const startedAt = this.toDate(startValue || null);
    const finishedAt = this.toDate(endValue || null);
    if (!startedAt) {
      return '-';
    }
    if (!finishedAt && !allowRunning) {
      return '-';
    }

    const end = finishedAt || new Date();
    let diffSeconds = Math.floor((end.getTime() - startedAt.getTime()) / 1000);
    if (!Number.isFinite(diffSeconds) || diffSeconds < 0) {
      return '-';
    }

    const hours = Math.floor(diffSeconds / 3600);
    diffSeconds -= hours * 3600;
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds - minutes * 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  isExecuting(transactionId: number) {
    return this.executingTransactionIds.has(transactionId);
  }

  isSavingValidation(transactionId: number) {
    return this.savingValidationIds.has(transactionId);
  }

  formatDate(value: string | null | undefined) {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    return date.toLocaleString('pt-BR');
  }

  shortText(value: string | null | undefined, max = 180) {
    if (!value) {
      return '-';
    }
    if (value.length <= max) {
      return value;
    }
    return `${value.slice(0, max)}...`;
  }

  formatPercent(value: any) {
    const num = Number(value);
    if (!Number.isFinite(num)) {
      return '0%';
    }
    return `${num.toFixed(2)}%`;
  }

  formatSecondsAsDuration(value: any) {
    const totalSecondsRaw = Number(value);
    if (!Number.isFinite(totalSecondsRaw) || totalSecondsRaw < 0) {
      return '-';
    }
    let totalSeconds = Math.round(totalSecondsRaw);
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds -= hours * 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds - minutes * 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  private buildReprocessReferenceId(sourceReference: string | null | undefined) {
    const base = String(sourceReference || `CAI-${Date.now()}`)
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 90);
    return `${base}-RP-${Date.now()}`.slice(0, 120);
  }

  private normalizeReceivedItemsForClone(value: any) {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      try {
        return JSON.parse(trimmed);
      } catch (_error) {
        return value;
      }
    }
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_error) {
      return value;
    }
  }

  private normalizeAnalysisCommentForClone(value: any, sourceTransactionId: number) {
    const original = String(value || '').trim();
    const reprocessNote = `Reprocessado automaticamente a partir da transação #${sourceTransactionId}.`;
    if (!original) {
      return reprocessNote;
    }
    if (original.includes(reprocessNote)) {
      return original;
    }
    return `${original}\n${reprocessNote}`.trim();
  }

  get commandsTotalCount() {
    return this.commands.length;
  }

  get commandsActiveCount() {
    return this.commands.filter((command) => Number(command.active) === 1).length;
  }

  get commandsControlchatEnabledCount() {
    return this.commands.filter((command) => Number(command.controlchat_enabled) === 1).length;
  }

  get commandsWithVisualMapCount() {
    return this.commands.filter((command) => Number(command.visual_assets_parsed?.length || 0) > 0).length;
  }

  get transactionsTotalCount() {
    return this.transactions.length;
  }

  get transactionsOpenCount() {
    return this.transactions.filter((tx) => String(tx.lifecycle_status || 'OPEN').toUpperCase() === 'OPEN').length;
  }

  get isTransactionDraftActive() {
    return Number(this.transactionForm?.transaction_id || 0) > 0;
  }
}
